import type { LogLevel } from './types';

type LogCategory = 
  | 'Initialization'
  | 'API'
  | 'Translation'
  | 'Navigation'
  | 'Maps'
  | 'Cache'
  | 'Events'
  | 'PWA'
  | 'Analytics'
  | 'Performance'
  | 'UI'
  | 'Routing'
  | 'Error';

interface LogOptions {
  category: LogCategory;
  data?: unknown;
}

class Logger {
  private static instance: Logger;
  private isDebugMode: boolean;
  private categoryColors: Record<LogCategory, string> = {
    Initialization: '#10b981',
    API: '#3b82f6',
    Translation: '#8b5cf6',
    Navigation: '#ec4899',
    Maps: '#f59e0b',
    Cache: '#6366f1',
    Events: '#14b8a6',
    PWA: '#f97316',
    Analytics: '#84cc16',
    Performance: '#ef4444',
    UI: '#06b6d4',
    Routing: '#8b5cf6',
    Error: '#dc2626'
  };

  private constructor() {
    this.isDebugMode = this.initializeDebugMode();
  }

  private initializeDebugMode(): boolean {
    try {
      if (import.meta.env.PROD) {
        return import.meta.env.VITE_FORCE_LOGS === 'true';
      }
      return true;
    } catch (error) {
      return process.env.NODE_ENV === 'development';
    }
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private formatMessage(level: LogLevel, message: string, options: LogOptions): string {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
    const category = `[${options.category}]`;
    return `${timestamp} ${level.toUpperCase()} ${category} ${message}`;
  }

  private logWithColor(level: LogLevel, message: string, options: LogOptions): void {
    if (!this.isDebugMode && level !== 'error') return;

    const color = this.categoryColors[options.category];
    const formattedMessage = this.formatMessage(level, message, options);
    
    let style = `color: ${color}; font-weight: bold;`;
    if (level === 'error') {
      style += ' background: #fee2e2; padding: 2px 4px; border-radius: 2px;';
    } else if (level === 'warn') {
      style += ' background: #fef3c7; padding: 2px 4px; border-radius: 2px;';
    }

    const emoji = {
      Initialization: '🚀',
      API: '🔌',
      Translation: '🌐',
      Navigation: '🧭',
      Maps: '🗺️',
      Cache: '💾',
      Events: '🎯',
      PWA: '📱',
      Analytics: '📊',
      Performance: '⚡',
      UI: '🎨',
      Routing: '🔄',
      Error: '❌'
    }[options.category];

    // Toujours afficher les logs en mode debug
    console.log(
      `%c${emoji} ${formattedMessage}`,
      style,
      options.data || ''
    );
  }

  public debug(message: string, options: LogOptions): void {
    this.logWithColor('debug', message, options);
  }

  public info(message: string, options: LogOptions): void {
    this.logWithColor('info', message, options);
  }

  public warn(message: string, options: LogOptions): void {
    this.logWithColor('warn', message, options);
  }

  public error(message: string, options: LogOptions): void {
    this.logWithColor('error', message, options);
  }
}

export const logger = Logger.getInstance();